'use client'

import { useState, lazy, Suspense, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Logo } from '@/components/ui/logo'
import {
  Users,
  FileText,
  Settings,
  Timer,
  Target,
  AlertTriangle,
  CheckCircle,
  Search,
  ArrowRight,
  Folder,
  PlayCircle,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Paperclip,
  Brain,
  Sparkles,
  LayoutGrid,
  RotateCcw
} from 'lucide-react'
import { getAuthHeaders } from '@/lib/api-utils'
import { getRedmineBaseUrl } from '@/lib/tenant-config'
import type { UserData } from '@/lib/types/ai'
import { useDashboardStats, useCurrentUser, useMetadata, useWeeklyTime } from '@/hooks/use-dashboard'
import { MetricCard } from '@/components/ui/metric-card'
import { Skeleton } from '@/components/ui/skeleton'
import { loadDashboardLayout, saveDashboardLayout, resetDashboardLayout } from '@/lib/dashboard-storage'
import { DEFAULT_LAYOUT_CONFIG, getGridColumns, type DashboardLayoutConfig, type GridLayout, type DashboardWidget } from '@/lib/dashboard-layout'
import { LayoutArrangerSidebar } from '@/components/ui/layout-arranger-sidebar'
import { QuickActionsWidget } from '@/components/shared/quick-actions-widget'
import { WeeklyActivityWidget } from '@/components/dashboard/widgets/weekly-activity-widget'
import { RecentActivityWidget } from '@/components/dashboard/widgets/recent-activity-widget'
import { ProjectsWidget } from '@/components/dashboard/widgets/projects-widget'
import { DeadlinesWidget } from '@/components/dashboard/widgets/deadlines-widget'
import { CompletionRateWidget } from '@/components/dashboard/widgets/completion-rate-widget'
import Masonry from 'react-masonry-css'

// Lazy load heavy components
const EditIssueModal = lazy(() => import('@/components/issues/edit-issue-modal').then(mod => ({ default: mod.EditIssueModal })))
const AICommandBar = lazy(() => import('@/components/dashboard/ai-command-bar').then(mod => ({ default: mod.AICommandBar })))
const AIInsightsSidebar = lazy(() => import('@/components/dashboard/ai-insights-sidebar').then(mod => ({ default: mod.AIInsightsSidebar })))

export default function HomePage() {
  const router = useRouter()
  const [tenantLoading, setTenantLoading] = useState(true)
  const [mounted, setMounted] = useState(false)
  const [tenantContext, setTenantContext] = useState<any>(null)
  const [hasTenant, setHasTenant] = useState(false)

  console.log('[HomePage] Render - mounted:', mounted, 'tenantLoading:', tenantLoading, 'tenantContext:', tenantContext, 'hasTenant:', hasTenant)

  // Handle hydration - only set mounted once
  useEffect(() => {
    console.log('[HomePage] Setting mounted to true')
    setMounted(true)
  }, [])

  // Check for tenant context on mount - do this once
  useEffect(() => {
    if (!mounted) {
      console.log('[HomePage] Skipping tenant check - not mounted yet')
      return
    }

    console.log('[HomePage] Starting tenant check...')
    const checkTenant = async () => {
      try {
        // Get tenant from cookie directly
        const cookies = document.cookie.split(';')
        const tenantCookie = cookies.find(c => c.trim().startsWith('tenant-context='))

        if (tenantCookie) {
          const cookieValue = tenantCookie.split('=').slice(1).join('=').trim()
          const tenantData = JSON.parse(decodeURIComponent(cookieValue))
          console.log('[HomePage] Found tenant:', tenantData.name)
          setTenantContext(tenantData)
          setHasTenant(true)
        } else {
          console.log('[HomePage] No tenant cookie found')
          setTenantContext(null)
          setHasTenant(false)
        }
      } catch (error) {
        console.error('[HomePage] Failed to fetch tenant:', error)
        setTenantContext(null)
        setHasTenant(false)
      } finally {
        console.log('[HomePage] Tenant check complete')
        setTenantLoading(false)
      }
    }

    checkTenant()
  }, [mounted])

  // Show loading state during hydration and tenant check
  if (!mounted || tenantLoading) {
    console.log('[HomePage] Showing loading state')
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // If no tenant context, show tenant selection message
  if (!hasTenant || !tenantContext) {
    console.log('[HomePage] No tenant, showing selection message')
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <Logo width={200} height={60} />
          </div>

          <Card className="w-full">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <h2 className="text-2xl font-bold">Welcome to Oxigen</h2>
                <p className="text-muted-foreground mt-4">
                  Please access this application through your tenant subdomain.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Example: <code className="bg-muted px-2 py-1 rounded">app.oxigen.oxinus.holdings</code>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // If we have a tenant, render the dashboard
  // AuthGuard in the layout will handle authentication and redirects
  console.log('[HomePage] Tenant exists, rendering DashboardPage')
  return <DashboardPage />
}

function DashboardPage() {
  const t = useTranslations()
  const tCommon = useTranslations('common')
  const tDashboard = useTranslations('dashboard')
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'

  // Use React Query hooks for data fetching with automatic caching
  const { data: dashboardData, isLoading: loadingStats, error: statsError } = useDashboardStats()
  const { data: currentUser } = useCurrentUser()
  const { data: metadata } = useMetadata()
  const { data: weeklyTimeEntries = [] } = useWeeklyTime()

  // Local state for UI interactions only
  const [showNewIssueModal, setShowNewIssueModal] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null)
  const [showProjectSelector, setShowProjectSelector] = useState(false)
  const [allProjects, setAllProjects] = useState<any[]>([])
  const [projectSearch, setProjectSearch] = useState('')
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set())
  const [showAISidebar, setShowAISidebar] = useState(false)

  // Dashboard layout state
  const [layoutConfig, setLayoutConfig] = useState<DashboardLayoutConfig>(DEFAULT_LAYOUT_CONFIG)
  const [showArrangerSidebar, setShowArrangerSidebar] = useState(false)

  // Load layout from localStorage on mount
  useEffect(() => {
    const savedLayout = loadDashboardLayout()
    setLayoutConfig(savedLayout)
  }, [])

  const loading = loadingStats
  const error = statsError ? (statsError as Error).message : null

  const stats = dashboardData || {
    assignedIssues: 0,
    recentActivity: [],
    upcomingDeadlines: [],
    timeSpentToday: 0,
    projectsCount: 0,
    projects: [],
    onTrackProjects: 0,
    overdueTasks: 0,
    completedThisWeek: 0
  }

  const getReadableFieldName = (name: string): string => {
    const fieldNameMap: Record<string, string> = {
      'status_id': 'Status',
      'tracker_id': 'Tracker',
      'priority_id': 'Priority',
      'assigned_to_id': 'Assignee',
      'fixed_version_id': 'Target Version',
      'category_id': 'Category',
      'subject': 'Subject',
      'description': 'Description',
      'done_ratio': '% Done',
      'estimated_hours': 'Estimated Time',
      'start_date': 'Start Date',
      'due_date': 'Due Date',
      'parent_id': 'Parent Task',
      'is_private': 'Private'
    }

    return fieldNameMap[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const getReadableValue = (property: string, name: string, value: string | undefined): string => {
    if (!value) return ''

    if (name === 'tracker_id' && metadata?.trackers) {
      const tracker = metadata.trackers.find(t => t.id === parseInt(value))
      return tracker?.name || value
    }

    if (name === 'status_id' && metadata?.statuses) {
      const status = metadata.statuses.find(s => s.id === parseInt(value))
      return status?.name || value
    }

    if (name === 'priority_id' && metadata?.priorities) {
      const priority = metadata.priorities.find(p => p.id === parseInt(value))
      return priority?.name || value
    }

    if (name === 'assigned_to_id' && metadata?.users) {
      const user = metadata.users.find(u => u.id === parseInt(value))
      const userName = user?.name || (user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : null)
      return userName || value
    }

    if (name === 'fixed_version_id') {
      return value
    }

    if (name === 'category_id') {
      return value
    }

    if (name === 'done_ratio' || name === '% Done') {
      return `${value}%`
    }

    if (name === 'is_private') {
      return value === '1' || value === 'true' ? 'Yes' : 'No'
    }

    return value
  }

  const isAttachment = (detail: any): boolean => {
    return detail.property === 'attachment' || detail.name?.includes('attachment') || /^\d+$/.test(detail.name)
  }

  const getAttachmentUrl = (attachmentId: string, issueId: number): string => {
    const baseUrl = getRedmineBaseUrl()
    return `${baseUrl}/attachments/download/${attachmentId}`
  }

  const fetchAllProjects = async () => {
    try {
      const authHeaders = getAuthHeaders()
      const response = await fetch('/api/projects', {
        headers: { ...authHeaders }
      })

      if (response.ok) {
        const data = await response.json()
        setAllProjects(data.projects || [])
      }
    } catch (err) {
      // Error fetching projects
    }
  }

  const handleNewIssueClick = () => {
    fetchAllProjects()
    setShowProjectSelector(true)
  }

  const handleProjectSelect = (projectId: number) => {
    setSelectedProjectId(projectId)
    setShowProjectSelector(false)
    setShowNewIssueModal(true)
  }

  const toggleProjectExpansion = (projectId: number) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }

  const buildProjectHierarchy = (projects: any[]) => {
    const projectMap = new Map()
    const rootProjects: any[] = []

    projects.forEach(project => {
      projectMap.set(project.id, { ...project, children: [] })
    })

    projects.forEach(project => {
      const projectNode = projectMap.get(project.id)
      if (project.parent && project.parent.id) {
        const parent = projectMap.get(project.parent.id)
        if (parent) {
          parent.children.push(projectNode)
        } else {
          rootProjects.push(projectNode)
        }
      } else {
        rootProjects.push(projectNode)
      }
    })

    return rootProjects
  }

  const filterProjects = (projects: any[], searchTerm: string): any[] => {
    if (!searchTerm) return projects

    const matchesSearch = (project: any): boolean => {
      const matches = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                     project.identifier.toLowerCase().includes(searchTerm.toLowerCase())

      if (matches) return true

      if (project.children && project.children.length > 0) {
        return project.children.some((child: any) => matchesSearch(child))
      }

      return false
    }

    const filterTree = (project: any): any | null => {
      const matches = matchesSearch(project)
      if (!matches) return null

      const filteredChildren = project.children
        ? project.children.map((child: any) => filterTree(child)).filter(Boolean)
        : []

      return {
        ...project,
        children: filteredChildren
      }
    }

    return projects.map(filterTree).filter(Boolean)
  }

  // Filter deadlines for next 5 days only
  const filteredDeadlines = stats.upcomingDeadlines
    .filter((deadline: any) => {
      if (!deadline.due_date) return false
      const dueDate = new Date(deadline.due_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const fiveDaysFromNow = new Date(today)
      fiveDaysFromNow.setDate(today.getDate() + 5)
      return dueDate >= today && dueDate <= fiveDaysFromNow
    })
    .sort((a: any, b: any) => {
      const dateA = new Date(a.due_date)
      const dateB = new Date(b.due_date)
      return dateA.getTime() - dateB.getTime()
    })

  const getWeeklyActivityData = () => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
    const weekDates: Date[] = []

    for (let i = 0; i < 5; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + mondayOffset + i)
      date.setHours(0, 0, 0, 0)
      weekDates.push(date)
    }

    const hoursPerDay = [0, 0, 0, 0, 0]

    weeklyTimeEntries.forEach((entry: any) => {
      const entryDate = new Date(entry.spent_on)
      entryDate.setHours(0, 0, 0, 0)

      for (let i = 0; i < 5; i++) {
        if (entryDate.getTime() === weekDates[i].getTime()) {
          hoursPerDay[i] += entry.hours || 0
          break
        }
      }
    })

    const chartData = dayNames.map((day, index) => ({
      day,
      hours: parseFloat(hoursPerDay[index].toFixed(1))
    }))

    return chartData
  }

  const weeklyChartData = getWeeklyActivityData()
  const totalWeeklyHours = weeklyChartData.reduce((sum, day) => sum + day.hours, 0)

  const handleWidgetsChange = (widgets: DashboardWidget[]) => {
    const newConfig = { ...layoutConfig, widgets }
    setLayoutConfig(newConfig)
    saveDashboardLayout(newConfig)
  }

  const handleGridLayoutChange = (gridLayout: GridLayout) => {
    const newConfig = { ...layoutConfig, gridLayout }
    setLayoutConfig(newConfig)
    saveDashboardLayout(newConfig)
  }

  const handleReset = () => {
    resetDashboardLayout()
    setLayoutConfig(DEFAULT_LAYOUT_CONFIG)
  }

  // Get visible widgets sorted by order
  const visibleWidgets = layoutConfig.widgets
    .filter(w => w.visible)
    .sort((a, b) => a.order - b.order)

  // Render widget by ID
  const renderWidget = (widget: DashboardWidget) => {
    switch (widget.id) {
      case 'quick-actions':
        return (
          <QuickActionsWidget
            key={widget.id}
            onCreateIssue={handleNewIssueClick}
            loading={loading}
            variant="dashboard"
          />
        )
      case 'weekly-activity':
        return (
          <WeeklyActivityWidget
            key={widget.id}
            title={t('dashboard.weeklyActivity')}
            data={weeklyChartData}
            totalHours={totalWeeklyHours}
            loading={loading}
          />
        )
      case 'recent-activity':
        return (
          <RecentActivityWidget
            key={widget.id}
            title={t('dashboard.recentActivity')}
            activities={stats.recentActivity}
            trackers={metadata?.trackers || []}
            statuses={metadata?.statuses || []}
            priorities={metadata?.priorities || []}
            users={metadata?.users || []}
            loading={loading}
            compact={layoutConfig.gridLayout === '3x3'}
          />
        )
      case 'projects':
        return (
          <ProjectsWidget
            key={widget.id}
            title={t('dashboard.myProjects')}
            projects={stats.projects}
            loading={loading}
          />
        )
      case 'deadlines':
        return (
          <DeadlinesWidget
            key={widget.id}
            deadlines={filteredDeadlines}
            loading={loading}
          />
        )
      case 'completion-rate':
        return (
          <CompletionRateWidget
            key={widget.id}
            completedThisWeek={stats.completedThisWeek}
            assignedIssues={stats.assignedIssues}
            loading={loading}
          />
        )
      default:
        return null
    }
  }

  // Masonry breakpoints - adjust for better distribution
  const masonryBreakpoints = {
    default: getGridColumns(layoutConfig.gridLayout),
    1280: getGridColumns(layoutConfig.gridLayout),
    1024: layoutConfig.gridLayout === '3x3' ? 2 : 2,
    768: 1
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              {loading ? (
                <>
                  <Skeleton className="h-8 w-32 mb-2 rounded-full" />
                  <Skeleton className="h-4 w-80 rounded-full" />
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-foreground">
                    {t('dashboard.title')}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('dashboard.subtitle')}
                  </p>
                </>
              )}
            </div>

            {/* Arrange and Reset buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArrangerSidebar(true)}
                className="flex items-center gap-2"
              >
                <LayoutGrid className="h-4 w-4" />
                {tCommon('arrange')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                {tCommon('reset')}
              </Button>
            </div>
          </div>
        </div>

        {/* AI Command Bar */}
        <div className="mb-6 flex gap-2 items-start">
          {loading ? (
            <>
              <Card className="border flex-1">
                <CardContent className="p-0">
                  <Skeleton className="h-12 w-full rounded-lg" />
                </CardContent>
              </Card>
              <Skeleton className="h-12 w-12 rounded-full" />
            </>
          ) : (
            <>
              <div className="flex-1">
                <Suspense fallback={<Skeleton className="h-12 w-full rounded-lg" />}>
                  <AICommandBar
                    userData={{
                      assignedCount: stats.assignedIssues,
                      overdueCount: stats.overdueTasks,
                      completedToday: stats.completedThisWeek,
                      activeProjects: stats.projects,
                      timeSpentToday: stats.timeSpentToday,
                      mail: currentUser?.mail,
                      login: currentUser?.login,
                      id: currentUser?.id,
                      firstname: currentUser?.firstname,
                      lastname: currentUser?.lastname
                    }}
                    currentPage="dashboard"
                  />
                </Suspense>
              </div>
              <Button
                onClick={() => setShowAISidebar(true)}
                size="icon"
                variant="outline"
                className="h-12 w-12 rounded-full border-2 hover:bg-primary/5 hover:border-primary transition-colors"
              >
                <Sparkles className="h-5 w-5 text-primary" />
              </Button>
            </>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <MetricCard
            label={t('dashboard.assigned')}
            value={stats.assignedIssues}
            icon={Target}
            loading={loading}
          />
          <MetricCard
            label={t('dashboard.overdue')}
            value={stats.overdueTasks}
            icon={AlertTriangle}
            loading={loading}
          />
          <MetricCard
            label={t('dashboard.completed')}
            value={stats.completedThisWeek}
            icon={CheckCircle}
            loading={loading}
          />
          <MetricCard
            label={t('dashboard.timeToday')}
            value={`${stats.timeSpentToday.toFixed(1)}h`}
            icon={Timer}
            loading={loading}
          />
        </div>

        {/* Dashboard Widgets with Masonry Layout */}
        <Masonry
          breakpointCols={masonryBreakpoints}
          className="flex -ml-4 md:-ml-6 w-full"
          columnClassName="pl-4 md:pl-6 bg-clip-padding flex-shrink-0"
        >
          {visibleWidgets.map(widget => (
            <div key={widget.id} className="mb-4 md:mb-6">
              {renderWidget(widget)}
            </div>
          ))}
        </Masonry>
      </div>

      {/* Dashboard Arranger Sidebar */}
      <LayoutArrangerSidebar
        isOpen={showArrangerSidebar}
        onClose={() => setShowArrangerSidebar(false)}
        widgets={layoutConfig.widgets}
        gridLayout={layoutConfig.gridLayout}
        onWidgetsChange={handleWidgetsChange}
        onGridLayoutChange={handleGridLayoutChange}
        title={tDashboard('arrangeTitle')}
      />

      {/* Project Selector Sidebar */}
      {showProjectSelector && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[10000] transition-opacity"
            onClick={() => setShowProjectSelector(false)}
          />

          {/* Sidebar */}
          <div
            className="fixed top-0 ltr:right-0 rtl:left-0 bottom-0 w-full sm:w-[500px] bg-background ltr:border-l rtl:border-r border-border z-[10001] flex flex-col shadow-2xl animate-in ltr:slide-in-from-right rtl:slide-in-from-left duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-background border-b border-border p-6 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Select Project</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowProjectSelector(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search projects..."
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  className="ltr:pl-10 rtl:pr-10"
                />
              </div>
            </div>

            {/* Project Tree */}
            <div className="overflow-y-auto flex-1 p-4">
              {(() => {
                const hierarchy = buildProjectHierarchy(allProjects)
                const filtered = filterProjects(hierarchy, projectSearch)

                const renderProject = (project: any, level: number = 0): React.ReactNode => {
                  const hasChildren = project.children && project.children.length > 0
                  const isExpanded = expandedProjects.has(project.id)

                  return (
                    <div key={project.id} className="mb-1">
                      <div
                        className="flex items-center gap-2 p-2 rounded-lg transition-colors group"
                        style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
                      >
                        {hasChildren ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleProjectExpansion(project.id)
                            }}
                            className="flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-muted rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : isRTL ? (
                              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        ) : (
                          <div className="w-5 h-5 flex-shrink-0" />
                        )}

                        <div
                          onClick={() => handleProjectSelect(project.id)}
                          className="flex items-center gap-2 flex-1 cursor-pointer min-w-0"
                        >
                          <Folder className="h-4 w-4 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{project.name}</div>
                          </div>
                          <span className="text-xs text-muted-foreground font-mono bg-muted px-2 py-0.5 rounded flex-shrink-0">
                            {project.identifier}
                          </span>
                        </div>
                      </div>

                      {hasChildren && isExpanded && (
                        <div>
                          {project.children.map((child: any) => renderProject(child, level + 1))}
                        </div>
                      )}
                    </div>
                  )
                }

                return filtered.length > 0 ? (
                  filtered.map((project) => renderProject(project, 0))
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Folder className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No projects found</p>
                  </div>
                )
              })()}
            </div>
          </div>
        </>
      )}

      {/* New Issue Modal */}
      {showNewIssueModal && selectedProjectId && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="bg-background rounded-lg p-8">Loading...</div></div>}>
          <EditIssueModal
            isOpen={showNewIssueModal}
            onClose={() => {
              setShowNewIssueModal(false)
              setSelectedProjectId(null)
            }}
            projectId={selectedProjectId}
            onSuccess={() => {
              setShowNewIssueModal(false)
              setSelectedProjectId(null)
            }}
          />
        </Suspense>
      )}

      {/* AI Insights Sidebar */}
      {showAISidebar && (
        <Suspense fallback={null}>
          <AIInsightsSidebar
            open={showAISidebar}
            onOpenChange={setShowAISidebar}
            userData={{
              assignedCount: stats.assignedIssues,
              overdueCount: stats.overdueTasks,
              completedToday: stats.completedThisWeek,
              activeProjects: stats.projects,
              timeSpentToday: stats.timeSpentToday
            }}
            recentActivity={stats.recentActivity}
          />
        </Suspense>
      )}
    </div>
  )
}
